package org.example.project.engineletter.layouts.sys

import org.example.project.engineletter.layouts.SupportLayoutSysCustomDocumentContainer

import androidx.compose.runtime.Composable
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

private const val WE_BERICHT_ROWS_PER_PAGE = 20

private data class WeBerichtPage(
    val section: String,
    val rows: List<All>,
    val startPosition: Int
)

@Composable
fun SysTextToPdfWEWareneingangsberichtLayout(
    rootElementId: String? = null,
    auftrag: All,
    webKontrolle: All,
    unternehmen: All,
    logoSrc: String,
    sideBySidePreview: Boolean = false
) {
    val pages = weWareneingangsberichtPages(webKontrolle)
    SupportLayoutSysCustomDocumentContainer(rootElementId = rootElementId, sideBySidePreview = sideBySidePreview) {
        pages.forEachIndexed { index, page ->
            WEVorgangslogPage {
                WEVorgangslogHeader(unternehmen = unternehmen, logoSrc = logoSrc)
                WEWareneingangsberichtTitleRow(auftrag = auftrag)
                WEWareneingangsberichtSmartTabular(page = page)
                Div({
                    style {
                        property("margin-top", "auto")
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.FlexEnd)
                        justifyContent(JustifyContent.SpaceBetween)
                        gap(18.px)
                    }
                }) {
                    WEVorgangslogSignature()
                    Div({
                        style {
                            fontSize(12.px)
                            lineHeight(16.px)
                            property("font-weight", "700")
                            textAlign("right")
                            paddingBottom(6.px)
                        }
                    }) {
                        Text("Seite ${index + 1} / ${pages.size}")
                    }
                }
            }
        }
    }
}

@Composable
private fun WEWareneingangsberichtTitleRow(auftrag: All) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(10.px)
            marginTop(55.px)
            marginBottom(18.px)
            textAlign("center")
            fontSize(16.px)
            lineHeight(20.px)
        }
    }) {
        Div({ style { fontSize(30.px); lineHeight(36.px); property("font-weight", "700") } }) {
            Text("Wareneingang Fehlerprotokoll")
        }
        WEProzessHeaderMetaRow(auftrag = auftrag)
    }
}

@Composable
private fun WEWareneingangsberichtSmartTabular(page: WeBerichtPage) {
    Div({
        style {
            border(1.px, LineStyle.Solid, Color("#111111"))
            property("box-sizing", "border-box")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
        }
    }) {
        Div({
            style {
                padding(7.px, 10.px)
                fontSize(14.px)
                lineHeight(18.px)
                property("font-weight", "700")
                textAlign("center")
                property("border-bottom", "1px solid #111111")
            }
        }) {
            Text(page.section)
        }
        WEWareneingangsberichtTableHeader()
        if (page.rows.isEmpty()) {
            Div({
                style {
                    minHeight(54.px)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(11.px)
                    lineHeight(15.px)
                }
            }) {
                Text("")
            }
        } else {
            page.rows.forEachIndexed { index, row ->
                WEWareneingangsberichtTableRow(row = row, position = page.startPosition + index)
            }
        }
    }
}

@Composable
private fun WEWareneingangsberichtTableHeader() {
    WEWareneingangsberichtGridRow(
        values = listOf("POS", "NVE / Charge", "L-Code", "MHD", "Artikel Nummer", "Menge"),
        isHeader = true
    )
}

@Composable
private fun WEWareneingangsberichtTableRow(row: All, position: Int) {
    WEWareneingangsberichtGridRow(
        values = listOf(
            "POS: ${position.toString().padStart(2, '0')}",
            row.nve,
            row.lcode,
            row.mhd,
            row.artikelNummer,
            row.menge
        ),
        isHeader = false
    )
}

@Composable
private fun WEWareneingangsberichtGridRow(values: List<String>, isHeader: Boolean) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "0.62fr 1.25fr 1fr 0.9fr 1.25fr 0.7fr")
            property("border-bottom", "1px solid ${if (isHeader) "#111111" else "rgba(17,17,17,0.30)"}")
            fontSize(if (isHeader) 10.px else 10.px)
            lineHeight(if (isHeader) 14.px else 14.px)
            property("font-weight", "700")
        }
    }) {
        values.forEachIndexed { index, value ->
            Div({
                style {
                    minHeight(if (isHeader) 26.px else 25.px)
                    padding(5.px, 7.px)
                    if (index < values.lastIndex) property("border-right", "1px solid rgba(17,17,17,0.52)")
                    property("box-sizing", "border-box")
                    property("word-break", "break-word")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(if (index == 0 || index == values.lastIndex) JustifyContent.Center else JustifyContent.FlexStart)
                    textAlign(if (index == 0 || index == values.lastIndex) "center" else "left")
                }
            }) { Text(value) }
        }
    }
}

private fun weWareneingangsberichtPages(webKontrolle: All): List<WeBerichtPage> =
    listOf("Bruch", "Fehlmenge", "Überzählig").flatMap { section ->
        webKontrolle.weWareneingangsberichtRows(section)
            .chunked(WE_BERICHT_ROWS_PER_PAGE)
            .mapIndexed { index, rows ->
                WeBerichtPage(
                    section = section,
                    rows = rows,
                    startPosition = index * WE_BERICHT_ROWS_PER_PAGE + 1
                )
            }
    }.ifEmpty {
        listOf(WeBerichtPage(section = "Fehlerprotokoll", rows = emptyList(), startPosition = 1))
    }

private fun All.weWareneingangsberichtRows(typ: String): List<All> =
    kontrollPositionen.filter { position ->
        position.typ.equals(typ, ignoreCase = true)
    }
